// Stableford runner. Pure function — given the round's course, players,
// scores, and the user-configured Stableford options, returns per-hole
// + per-player point allocations.
//
// No state, no side effects, no React. The runner is consumed by
// LeaderboardModal (and any other UI surface that wants to show
// Stableford points). Same shape that future runners will return so
// the leaderboard can iterate uniformly.

import { Course, Hole, Player, ScoreMap } from "../types";
import { HandicapMode, adjustHandicaps } from "../handicapAdjust";

// WHS-style course handicap (mirrors the helper in Scorecard.tsx).
// Duplicated here so the runner has no React/component dependencies.
function courseHandicap(
  handicapIndex: number | undefined,
  slope: number | undefined,
  rating: number | undefined,
  totalPar: number,
): number {
  if (handicapIndex == null) return 0;
  const slopeFactor = slope ? slope / 113 : 1;
  const ratingDiff = rating != null ? rating - totalPar : 0;
  return Math.round(handicapIndex * slopeFactor + ratingDiff);
}

// Strokes received by a player on a single hole, given their full
// course handicap. Same distribution Scorecard uses for the ★ indicators.
function strokesOnHole(courseHcp: number, hole: Hole): number {
  if (courseHcp <= 0) return 0;
  const base = Math.floor(courseHcp / 18);
  const remainder = courseHcp % 18;
  return base + (hole.si <= remainder ? 1 : 0);
}

export type TeamSetup = "1-2v3-4" | "1-3v2-4" | "1-4v2-3";

export interface StablefordOptions {
  // Stableford is always scored on net (gross − strokes received).
  // Gross-Stableford was removed from the catalog; we keep the runner
  // deterministic so casual users without a registered handicap still
  // get a reasonable result (course handicap defaults to 0 → strokes
  // received is 0 on every hole → net equals gross for those players).
  scoringScale: "standard" | "modified" | "modifiedPga";
  // Bet-pool config — surfaced in the UI but not consumed by the
  // points calculation itself. The runner takes them in so it can
  // own pot allocation in a future pass; for now they're just
  // pass-through fields.
  overallBet?: number;
  nassau?: boolean;
  frontNineBet?: number;
  backNineBet?: number;
  // Non-Nassau settlement mode. When `useBetPerPoint` is true and
  // Nassau is off, the runner switches the Overall bucket from a
  // pot-style settlement (overallBet) to head-to-head per-point
  // transfers using `betPerPoint`. Nassau ON ignores both —
  // each Nassau segment stays pot-style.
  betPerPoint?: number;
  useBetPerPoint?: boolean;
  // Tournament: when true the entry fee/ante was collected up front, so pot
  // settlements show gross winnings (winner's full share, $0 for others) with
  // no stake deducted. Off → current net settle-up (winner nets share − stake,
  // others show −stake). Only affects pot-style settlements.
  entryFeePaid?: boolean;
  // When `team` is on, every bet (Overall + any active Nassau segments)
  // settles as two-player teams instead of N-way individuals. Team
  // pairing comes from `teamSetup`. Team mode is only honoured when the
  // round has exactly four players; otherwise we silently fall back to
  // individual settlement.
  team?: boolean;
  teamSetup?: TeamSetup;
  // How each team's per-hole points are aggregated:
  //   • "aggregate"  → sum of both members' per-hole points (default)
  //   • "betterBall" → highest points per hole, summed across the segment
  // Only consulted in team mode; ignored otherwise.
  teamFormat?: TeamFormat;
  // Per-game handicap policy. "playOffLow" anchors the lowest player
  // to 0 strokes before WHS conversion so Stableford settles relative
  // to the field's best player. Other games in the same round are
  // free to keep using full handicaps.
  handicapMode?: HandicapMode;
}

export type TeamFormat = "aggregate" | "betterBall";

// Decode a teamSetup token into the two two-player teams. Order of
// players within a team doesn't affect scoring.
function teamsFromSetup<T>(players: T[], setup?: TeamSetup): [T[], T[]] {
  const [a, b, c, d] = players;
  if (setup === "1-3v2-4") return [[a, c], [b, d]];
  if (setup === "1-4v2-3") return [[a, d], [b, c]];
  return [[a, b], [c, d]];
}

export interface StablefordPayout {
  // Net dollars from each segment (winnings minus stake). 0 when the
  // segment isn't in play or the player didn't participate. Same shape
  // as Stroke Play so the leaderboard can render both uniformly — only
  // the win condition (highest points vs lowest strokes) differs.
  overall: number;
  front: number;
  back: number;
  total: number;
}

export interface StablefordResult {
  // points[playerId][holeNumber] = points scored on that hole.
  // Players who didn't card a score on a hole get no entry — the
  // caller decides how to render that (blank cell vs zero).
  points: Record<string, Record<number, number>>;
  // Per-player Stableford totals across the round.
  totals: Record<string, number>;
  // Betting payouts per player, computed from the overall + optional
  // Nassau front/back sub-bets. Highest Stableford total wins each
  // segment; ties split the pot.
  payouts: Record<string, StablefordPayout>;
  // True when at least one segment has a bet > 0. Drives whether the
  // leaderboard renders the Stableford betting sub-board.
  hasAnyBet: boolean;
  // True when Nassau is on — leaderboard switches to OUT / IN / TOTAL
  // columns when so.
  nassauActive: boolean;
}

export interface StablefordInput {
  course: Course;
  players: Player[];
  scores: ScoreMap;
  options: StablefordOptions;
}

// Net score → Stableford points, given the configured scale.
//   "standard"    : 1 bogey, 2 par, 3 birdie, 4 eagle, 5 albatross …
//                   (worse than bogey = 0)
//   "modified"    : 1 double-bogey, 2 bogey, 3 par, 4 birdie,
//                   5 eagle, 6 albatross …  (worse than dbl bogey = 0)
//   "modifiedPga" : -1 bogey, 0 par, +2 birdie, +5 eagle, +8 albatross
//                   (PGA Tour scale; worse than bogey = -3)
// Cap positive scoring at +10 to keep absurd hole-outs sane.
function pointsForNetScore(
  netStrokes: number,
  par: number,
  scale: "standard" | "modified" | "modifiedPga",
): number {
  const diff = netStrokes - par; // negative = under par

  if (scale === "standard") {
    if (diff >= 2) return 0;
    // diff = 1 (bogey) → 1; 0 (par) → 2; -1 (birdie) → 3; etc.
    return Math.min(10, 2 - diff);
  }

  if (scale === "modified") {
    if (diff >= 3) return 0;
    // diff = 2 (dbl bogey) → 1; 1 (bogey) → 2; 0 (par) → 3; etc.
    return Math.min(10, 3 - diff);
  }

  // modifiedPga
  if (diff >= 2) return -3;
  if (diff === 1) return -1;
  if (diff === 0) return 0;
  if (diff === -1) return 2;
  if (diff === -2) return 5;
  return 8 + (-2 - diff); // -3 (4-under) and beyond
}

export function runStableford(input: StablefordInput): StablefordResult {
  const { course, players, scores, options } = input;
  const totalPar = course.holes.reduce((s, h) => s + h.par, 0);
  const tees = course.tees || [];

  // Apply this game's handicap policy before deriving course handicaps.
  // "full" → identity. "playOffLow" → lowest player's index anchored to
  // 0, others carry the delta. The stored Player.handicap stays intact
  // (adjustHandicaps returns a fresh array).
  const adjustedPlayers = adjustHandicaps(players, options.handicapMode);

  // Cache per-player course handicap so we compute once, not per hole.
  const courseHcpById: Record<string, number> = {};
  for (const p of adjustedPlayers) {
    const tee = tees.find((t) => t.id === p.tee);
    courseHcpById[p.id] = courseHandicap(
      p.handicap,
      tee?.slope,
      tee?.rating,
      totalPar,
    );
  }

  const points: Record<string, Record<number, number>> = {};
  const totals: Record<string, number> = {};

  for (const p of players) {
    points[p.id] = {};
    totals[p.id] = 0;
    const playerScores = scores[p.id] || {};
    const ch = courseHcpById[p.id];

    for (const hole of course.holes) {
      const score = playerScores[hole.number];
      if (!score || typeof score.gross !== "number") continue;

      // Net adjustment: gross minus strokes received on this hole.
      // Always net — gross-Stableford option was removed. Players with
      // no registered handicap get courseHcp = 0, so strokes received
      // is 0 and net equals gross naturally.
      const netStrokes = score.gross - strokesOnHole(ch, hole);

      const pts = pointsForNetScore(
        netStrokes,
        hole.par,
        options.scoringScale,
      );

      points[p.id][hole.number] = pts;
      totals[p.id] += pts;
    }
  }

  // ── Betting settlement ──────────────────────────────────────────────────
  // Same pot/tie-split mechanics as Stroke Play, but highest points
  // wins each segment (Stableford is high-card-wins by design).
  const overallBet = Number(options.overallBet) || 0;
  const frontBet = options.nassau ? Number(options.frontNineBet) || 0 : 0;
  const backBet = options.nassau ? Number(options.backNineBet) || 0 : 0;
  // Entry fee collected up front → show gross pot winnings (no stake deducted).
  const feePaid = options.entryFeePaid === true;

  const payouts: Record<string, StablefordPayout> = {};
  for (const p of players) {
    payouts[p.id] = { overall: 0, front: 0, back: 0, total: 0 };
  }

  // Team mode applies to every active bet (Overall and the optional
  // Nassau front/back segments). Requires exactly four players so the
  // teamSetup pairing resolves cleanly; any other arrangement falls
  // back to individual settlement.
  const teamMode = !!options.team && players.length === 4;

  const segmentPoints = (
    pid: string,
    inSegment: (hole: number) => boolean,
  ): { pts: number; hasAny: boolean } => {
    const playerPts = points[pid] || {};
    let pts = 0;
    let hasAny = false;
    Object.entries(playerPts).forEach(([holeStr, val]) => {
      const h = parseInt(holeStr, 10);
      if (inSegment(h)) {
        pts += val;
        hasAny = true;
      }
    });
    return { pts, hasAny };
  };

  // Per-point individual settlement used when the user picks the
  // "Bet per Point" toggle (Nassau off). Every ordered pair of scored
  // players moves (myPts − theirPts) × bet into the player's bucket.
  // The table stays zero-sum and a tie produces no net movement.
  const settlePerPointIndividual = (
    inSegment: (hole: number) => boolean,
    bet: number,
    bucket: "overall" | "front" | "back",
  ) => {
    if (bet <= 0) return;
    const segScores: Array<{ id: string; pts: number }> = [];
    for (const p of players) {
      const s = segmentPoints(p.id, inSegment);
      if (s.hasAny) segScores.push({ id: p.id, pts: s.pts });
    }
    if (segScores.length < 2) return;
    for (const a of segScores) {
      for (const b of segScores) {
        if (a.id === b.id) continue;
        payouts[a.id][bucket] += (a.pts - b.pts) * bet;
      }
    }
  };

  // Per-point team settlement (Nassau off, useBetPerPoint on, team
  // mode on): each team's segment total is compared once and every
  // teammate on a side picks up (myTeam − theirTeam) × bet. Both
  // teammates land on the same net; the four-player table stays
  // zero-sum.
  const settlePerPointTeam = (
    inSegment: (hole: number) => boolean,
    bet: number,
    bucket: "overall" | "front" | "back",
  ) => {
    if (bet <= 0) return;
    const teams = teamsFromSetup(players, options.teamSetup);
    const format: TeamFormat =
      options.teamFormat === "betterBall" ? "betterBall" : "aggregate";
    const teamScores = teams.map((team) => {
      const s = teamSegmentPoints(team, inSegment, format);
      return { team, pts: s.pts, hasAny: s.hasAny };
    });
    const playing = teamScores.filter((t) => t.hasAny);
    if (playing.length < 2) return;
    const [a, b] = playing;
    const aNet = (a.pts - b.pts) * bet;
    const bNet = (b.pts - a.pts) * bet;
    for (const p of a.team) payouts[p.id][bucket] += aNet;
    for (const p of b.team) payouts[p.id][bucket] += bNet;
  };

  // Pot-style individual settlement. Each scored player antes `bet`
  // into a pot sized (N scored players) × bet; the highest segment-
  // points total wins, ties split. Applied per segment — Overall
  // alone with Nassau off, or all three (Front 9 / Back 9 / Overall)
  // with Nassau on.
  const settleIndividual = (
    inSegment: (hole: number) => boolean,
    bet: number,
    bucket: "overall" | "front" | "back",
  ) => {
    if (bet <= 0) return;
    const segScores: Array<{ id: string; pts: number }> = [];
    for (const p of players) {
      const s = segmentPoints(p.id, inSegment);
      if (s.hasAny) segScores.push({ id: p.id, pts: s.pts });
    }
    if (segScores.length === 0) return;
    const maxPts = Math.max(...segScores.map((s) => s.pts));
    const winners = segScores.filter((s) => s.pts === maxPts);
    const pot = segScores.length * bet;
    const share = pot / winners.length;
    for (const sp of segScores) {
      // Fee paid up front → gross (winner: share, others: 0). Otherwise net
      // (winner: share − stake, others: −stake).
      payouts[sp.id][bucket] +=
        sp.pts === maxPts
          ? feePaid
            ? share
            : share - bet
          : feePaid
            ? 0
            : -bet;
    }
  };

  // Per-hole team aggregator for Stableford. Walks every hole touched
  // by either teammate, in-segment only, and combines per-hole points
  // under the chosen team format:
  //   • aggregate  → sum of both teammates' per-hole points
  //   • betterBall → max of the two per-hole point totals
  // Holes with only one teammate scored use that lone value so live
  // leaderboards still surface progress mid-round.
  const teamSegmentPoints = (
    team: Player[],
    inSegment: (hole: number) => boolean,
    format: TeamFormat,
  ): { pts: number; hasAny: boolean } => {
    const holes = new Set<number>();
    for (const p of team) {
      for (const k of Object.keys(points[p.id] || {})) {
        holes.add(parseInt(k, 10));
      }
    }
    let pts = 0;
    let hasAny = false;
    for (const h of holes) {
      if (!inSegment(h)) continue;
      const holePts: number[] = [];
      for (const p of team) {
        const v = points[p.id]?.[h];
        if (typeof v === "number") holePts.push(v);
      }
      if (holePts.length === 0) continue;
      hasAny = true;
      if (format === "betterBall") pts += Math.max(...holePts);
      else pts += holePts.reduce((a, b) => a + b, 0);
    }
    return { pts, hasAny };
  };

  // Team mode pot settlement: P1+P2 vs P3+P4. Each team gets a single
  // segment total under the configured teamFormat (aggregate sum or
  // better-ball pick). Highest team wins. Pot = totalPlayers × bet;
  // winning team's members split equally. Ties refund.
  const settleTeam = (
    inSegment: (hole: number) => boolean,
    bet: number,
    bucket: "overall" | "front" | "back",
  ) => {
    if (bet <= 0) return;
    const teams = teamsFromSetup(players, options.teamSetup);
    const format: TeamFormat =
      options.teamFormat === "betterBall" ? "betterBall" : "aggregate";
    const teamScores = teams.map((team) => {
      const s = teamSegmentPoints(team, inSegment, format);
      return { team, pts: s.pts, hasAny: s.hasAny };
    });
    const playing = teamScores.filter((t) => t.hasAny);
    if (playing.length < 2) return;
    const maxPts = Math.max(...playing.map((t) => t.pts));
    const winningTeams = playing.filter((t) => t.pts === maxPts);
    const totalPlayers = playing.reduce((sum, t) => sum + t.team.length, 0);
    const pot = totalPlayers * bet;
    const winnerPlayerCount = winningTeams.reduce(
      (sum, t) => sum + t.team.length,
      0,
    );
    const perWinnerShare = pot / winnerPlayerCount;
    for (const t of playing) {
      const won = t.pts === maxPts;
      for (const p of t.team) {
        payouts[p.id][bucket] +=
          won
            ? feePaid
              ? perWinnerShare
              : perWinnerShare - bet
            : feePaid
              ? 0
              : -bet;
      }
    }
  };

  const settle = teamMode ? settleTeam : settleIndividual;

  // Non-Nassau settlement of the Overall bucket. Branches on the
  // user-facing toggle:
  //   • useBetPerPoint = false → pot-style with overallBet (default)
  //   • useBetPerPoint = true  → per-point with betPerPoint
  // Nassau ON ignores the toggle entirely and runs all three segments
  // through the pot-style settler — that's the long-standing
  // three-pot Nassau format.
  if (!options.nassau) {
    if (options.useBetPerPoint === true) {
      const ppBet = Number(options.betPerPoint) || 0;
      const settlePerPoint = teamMode
        ? settlePerPointTeam
        : settlePerPointIndividual;
      settlePerPoint((h) => h >= 1 && h <= 18, ppBet, "overall");
    } else {
      settle((h) => h >= 1 && h <= 18, overallBet, "overall");
    }
  } else {
    settle((h) => h >= 1 && h <= 18, overallBet, "overall");
    settle((h) => h >= 1 && h <= 9, frontBet, "front");
    settle((h) => h >= 10 && h <= 18, backBet, "back");
  }

  for (const p of players) {
    const pay = payouts[p.id];
    pay.total = pay.overall + pay.front + pay.back;
  }

  // hasAnyBet drives whether the leaderboard renders the betting
  // sub-board at all. Nassau ON: any of the three segment bets > 0.
  // Nassau OFF: depends on the toggle — useBetPerPoint reads
  // betPerPoint, otherwise overallBet (matching what the settle
  // branch above actually consumes).
  const ppBetValue = Number(options.betPerPoint) || 0;
  const nonNassauActive =
    options.useBetPerPoint === true ? ppBetValue > 0 : overallBet > 0;
  const hasAnyBet = options.nassau
    ? overallBet > 0 || frontBet > 0 || backBet > 0
    : nonNassauActive;

  return {
    points,
    totals,
    payouts,
    hasAnyBet,
    nassauActive: !!options.nassau,
  };
}
